import { toDisplayDate, toIsoDate } from '@/utils/dateString';
import { emergencyRelationLabel, emergencyRelationSelectOptions } from '@/shared/constants/emergencyRelation';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAuthedFileBlob } from '@/shared/hooks/useAuthedFileBlob';
import {
  X,
  User,
  Phone,
  FileText,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
  Eye,
  Trash2,
  GraduationCap,
  Briefcase,
  Users,
  BookOpen,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Award,
  Download,
  ExternalLink,
  ZoomIn,
  ZoomOut,
  RotateCw,
  Pencil,
  RotateCcw,
  Keyboard,
} from 'lucide-react';
import {
  getValidationRequest,
  reviewValidation,
  clearProfileField,
  deleteValidationDocument,
  updateProfileField,
  getAdminUserDocuments,
  type ValidationRequestDetails,
} from '@/services/validationService';
import {
  fetchSeriesBac,
  fetchProvinces,
  fetchDiplomes,
  fetchCountries,
  fetchHandicaps,
  type NomenclatureSerieBac,
  type NomenclatureProvince,
  type NomenclatureDiplome,
  type NomenclatureCountry,
  type NomenclatureHandicap,
} from '@/services/nomenclatureService';

interface ProfileValidationModalProps {
  isOpen: boolean;
  onClose: () => void;
  validationRequestId: string;
  onValidationComplete: () => void;
}

// Field labels for rejection and display - organized by section
const FIELD_LABELS: Record<string, string> = {
  // Section 1: Identité
  nom: 'Nom',
  prenom: 'Prénom',
  nomArabe: 'Nom (Arabe)',
  prenomArabe: 'Prénom (Arabe)',
  cnie: 'CNIE',
  passport: 'Passeport',
  sexe: 'Sexe',
  dateNaissance: 'Date de naissance',
  paysOrigine: 'Pays d\'origine',
  handicap: 'Situation de handicap',
  // Section 2: Coordonnées
  email: 'Email',
  telephone: 'Téléphone',
  adresse: 'Adresse',
  ville: 'Ville',
  codePostal: 'Code postal',
  phoneCountry: 'Indicatif téléphonique',
  // Section 3: Contact d'urgence
  urgenceNom: 'Contact d\'urgence - Nom',
  urgenceRelation: 'Contact d\'urgence - Relation',
  urgenceNumero: 'Contact d\'urgence - Numéro',
  urgenceAdresse: 'Contact d\'urgence - Adresse',
  urgencePhoneCountry: 'Contact d\'urgence - Indicatif',
  // Section 4: Inscription Académique (Student)
  numeroMassarCNE: 'N° Massar/CNE',
  diplomeInscrit: 'Diplôme préparé',
  filiereInscrit: 'Filière',
  niveauFormation: 'Niveau',
  situation: 'Situation',
  premiereAnneeInscription: 'Première année d\'inscription',
  campus: 'Campus',
  // Section 5: Diplôme d'Accès (Student)
  serieBaccalaureat: 'Série du Baccalauréat',
  anneeObtentionBac: 'Année d\'obtention du Bac',
  diplomeAcces: 'Diplôme d\'accès',
  specialiteDiplomeAcces: 'Spécialité du diplôme',
  mentionDiplomeAcces: 'Mention',
  lieuObtentionDiplomeAcces: 'Lieu d\'obtention',
  // Section 6: Statut & Aides (Student)
  resident: 'Résident',
  fonctionnaire: 'Fonctionnaire',
  pourcentageBourse: 'Pourcentage de bourse',
  mobilite: 'Mobilité',
  // Staff fields
  fonction: 'Fonction',
  rib: 'RIB',
  salaireMensuelNet: 'Salaire mensuel net',
  diplomeId: 'Diplôme',
  dateRecrutement: 'Date de recrutement',
  // Professor fields
  statutProfesseur: 'Statut professeur',
  departementAttacheId: 'Département',
  gradeId: 'Grade',
  specialite: 'Spécialité',
  matieresEnseignees: 'Matières enseignées',
  tauxHoraire: 'Taux horaire',
  estOriginaireEtablissementPublic: 'Originaire d\'établissement public',
  etablissementOrigine: 'Établissement d\'origine',
  // Parent fields
  relationAvecEtudiant: 'Relation avec l\'étudiant',
  relationPrecision: 'Précision de la relation',
  // Documents
  photo: 'Photo d\'identité',
  cinRecto: 'CIN Recto',
  cinVerso: 'CIN Verso',
  passportScan: 'Scan passeport',
  cv: 'CV',
  diplome: 'Diplôme',
  relevesNotes: 'Relevés de notes',
  autorisationEnseigner: 'Autorisation d\'enseigner',
  engagementProfesseur: 'Engagement professeur',
};

// Handicap labels
const HANDICAP_LABELS: Record<string, string> = {
  N: 'Aucun',
  A: 'Auditif',
  M: 'Moteur',
  V: 'Visuel',
  AM: 'Auditif + Moteur',
  AV: 'Auditif + Visuel',
  MV: 'Moteur + Visuel',
  T: 'Total',
  XX: 'Autre',
};

// Oui/Non labels
const OUI_NON_LABELS: Record<string, string> = {
  O: 'Oui',
  N: 'Non',
};

// Situation labels
const SITUATION_LABELS: Record<string, string> = {
  N: 'Nouveau',
  R: 'Redoublant',
  P: 'Passant',
};

// Mention labels
const MENTION_LABELS: Record<string, string> = {
  p: 'Passable',
  ab: 'Assez Bien',
  b: 'Bien',
  tb: 'Très Bien',
  e: 'Excellent',
  ns: 'Non Spécifié',
};

// Relation labels (for parent-student relationships)
const RELATION_LABELS: Record<string, string> = {
  PERE: 'Père',
  MERE: 'Mère',
  TUTEUR_LEGAL: 'Tuteur légal',
  GRAND_PARENT: 'Grand-parent',
  ONCLE_TANTE: 'Oncle/Tante',
  AUTRE: 'Autre',
};

// Emergency contact relationship labels (for staff urgenceRelation field)
// Role labels
const ROLE_LABELS: Record<string, string> = {
  STUDENT: 'Étudiant',
  TEACHER: 'Professeur',
  PARENT: 'Parent',
  ACADEMIC_COORDINATOR: 'Coordinateur académique',
  PROGRAM_HEAD: 'Chef de filière',
  FINANCIAL_MANAGER: 'Responsable financier',
  MARKETING_MANAGER: 'Responsable marketing',
  COMMERCIAL_MANAGER: 'Responsable commercial',
  BRANCH_MANAGER: 'Responsable d\'antenne',
  PEDAGOGICAL_DIRECTOR: 'Directeur pédagogique',
  DIRECTOR_GENERAL: 'Directeur général',
};

// Sexe options
const SEXE_OPTIONS = [
  { value: 'M', label: 'Masculin' },
  { value: 'F', label: 'Féminin' },
];

// Generate years for dropdowns
const generateYears = () => {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let year = currentYear; year >= 1970; year--) {
    years.push(year.toString());
  }
  return years;
};

// Arabic keyboard layout
const ARABIC_KEYBOARD_LAYOUT = [
  ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج'],
  ['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك', 'ط'],
  ['ئ', 'ء', 'ؤ', 'ر', 'لا', 'ى', 'ة', 'و', 'ز', 'ظ', 'د'],
];

// Virtual Arabic Keyboard Component
const VirtualArabicKeyboard: React.FC<{
  onKeyPress: (char: string) => void;
  onBackspace: () => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
}> = ({ onKeyPress, onBackspace, onClose, anchorRef }) => {
  const [position, setPosition] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 300 });

  useEffect(() => {
    const updatePosition = () => {
      if (anchorRef.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        const keyboardHeight = 180;
        const viewportHeight = window.innerHeight;
        const showAbove = rect.bottom + keyboardHeight > viewportHeight && rect.top > keyboardHeight;

        setPosition({
          top: showAbove ? rect.top - keyboardHeight - 5 : rect.bottom + 5,
          left: rect.left,
          width: Math.max(rect.width, 280),
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <div
      data-arabic-keyboard
      className="fixed z-[9999] p-2 bg-white border border-gray-300 rounded-lg shadow-xl"
      style={{ top: position.top, left: position.left, width: position.width }}
    >
      <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-200">
        <span className="text-xs text-gray-500 font-medium">لوحة المفاتيح العربية</span>
        <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="space-y-1">
        {ARABIC_KEYBOARD_LAYOUT.map((row, rowIndex) => (
          <div key={rowIndex} className="flex justify-center gap-0.5">
            {row.map((char, charIndex) => (
              <button
                key={charIndex}
                type="button"
                onClick={() => onKeyPress(char)}
                className="w-7 h-8 text-sm font-medium bg-gray-100 hover:bg-primary-100 hover:text-primary-700 rounded transition-colors border border-gray-200"
              >
                {char}
              </button>
            ))}
          </div>
        ))}
        <div className="flex justify-center gap-1 mt-2">
          <button
            type="button"
            onClick={() => onKeyPress(' ')}
            className="flex-1 h-8 text-xs bg-gray-100 hover:bg-gray-200 rounded border border-gray-200"
          >
            espace
          </button>
          <button
            type="button"
            onClick={onBackspace}
            className="w-16 h-8 text-xs bg-red-50 hover:bg-red-100 text-red-600 rounded border border-red-200"
          >
            ← effacer
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: string;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  children,
  defaultOpen = true,
  badge,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium text-gray-900">{title}</span>
          {badge && (
            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {isOpen && <div className="p-4 bg-white">{children}</div>}
    </div>
  );
};

// Field types for editing
type FieldType = 'text' | 'select' | 'date' | 'number' | 'arabic' | 'phone' | 'email';

interface FieldConfig {
  type: FieldType;
  options?: { value: string; label: string }[];
  optionsKey?: 'countries' | 'handicaps' | 'seriesBac' | 'provinces' | 'diplomes' | 'sexe' | 'ouiNon' | 'situation' | 'mention' | 'years' | 'relation' | 'emergencyRelation' | 'statutProfesseur' | 'phoneCountry';
  // For phone fields, specify which field stores the country code
  phoneCountryField?: string;
}

// Statut professeur labels
const STATUT_PROFESSEUR_OPTIONS = [
  { value: 'P', label: 'Permanent' },
  { value: 'V', label: 'Vacataire' },
];

// Country phone codes with flags
const COUNTRY_PHONE_CODES = [
  { code: 'MA', name: 'Maroc', dialCode: '+212', flag: '🇲🇦' },
  { code: 'DZ', name: 'Algérie', dialCode: '+213', flag: '🇩🇿' },
  { code: 'TN', name: 'Tunisie', dialCode: '+216', flag: '🇹🇳' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'ES', name: 'Espagne', dialCode: '+34', flag: '🇪🇸' },
  { code: 'BE', name: 'Belgique', dialCode: '+32', flag: '🇧🇪' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'US', name: 'États-Unis', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'Royaume-Uni', dialCode: '+44', flag: '🇬🇧' },
  { code: 'DE', name: 'Allemagne', dialCode: '+49', flag: '🇩🇪' },
  { code: 'IT', name: 'Italie', dialCode: '+39', flag: '🇮🇹' },
  { code: 'NL', name: 'Pays-Bas', dialCode: '+31', flag: '🇳🇱' },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
  { code: 'SA', name: 'Arabie Saoudite', dialCode: '+966', flag: '🇸🇦' },
  { code: 'AE', name: 'Émirats Arabes Unis', dialCode: '+971', flag: '🇦🇪' },
  { code: 'EG', name: 'Égypte', dialCode: '+20', flag: '🇪🇬' },
  { code: 'SN', name: 'Sénégal', dialCode: '+221', flag: '🇸🇳' },
  { code: 'CI', name: "Côte d'Ivoire", dialCode: '+225', flag: '🇨🇮' },
  { code: 'ML', name: 'Mali', dialCode: '+223', flag: '🇲🇱' },
  { code: 'MR', name: 'Mauritanie', dialCode: '+222', flag: '🇲🇷' },
];

// Field configurations for editing
const FIELD_CONFIGS: Record<string, FieldConfig> = {
  // Identity fields
  nom: { type: 'text' },
  prenom: { type: 'text' },
  nomArabe: { type: 'arabic' },
  prenomArabe: { type: 'arabic' },
  cnie: { type: 'text' },
  passport: { type: 'text' },
  sexe: { type: 'select', optionsKey: 'sexe' },
  dateNaissance: { type: 'date' },
  paysOrigine: { type: 'select', optionsKey: 'countries' },
  handicap: { type: 'select', optionsKey: 'handicaps' },
  // Contact fields
  email: { type: 'email' },
  telephone: { type: 'phone', phoneCountryField: 'phoneCountry' },
  phoneCountry: { type: 'select', optionsKey: 'phoneCountry' },
  adresse: { type: 'text' },
  urgenceNom: { type: 'text' },
  urgenceRelation: { type: 'select', optionsKey: 'emergencyRelation' },
  urgenceNumero: { type: 'phone', phoneCountryField: 'urgencePhoneCountry' },
  urgencePhoneCountry: { type: 'select', optionsKey: 'phoneCountry' },
  urgenceAdresse: { type: 'text' },
  // Student academic fields
  numeroMassarCNE: { type: 'text' },
  serieBaccalaureat: { type: 'select', optionsKey: 'seriesBac' },
  anneeObtentionBac: { type: 'select', optionsKey: 'years' },
  diplomeAcces: { type: 'select', optionsKey: 'diplomes' },
  specialiteDiplomeAcces: { type: 'text' },
  mentionDiplomeAcces: { type: 'select', optionsKey: 'mention' },
  lieuObtentionDiplomeAcces: { type: 'select', optionsKey: 'provinces' },
  resident: { type: 'select', optionsKey: 'ouiNon' },
  fonctionnaire: { type: 'select', optionsKey: 'ouiNon' },
  pourcentageBourse: { type: 'number' },
  mobilite: { type: 'select', optionsKey: 'ouiNon' },
  situation: { type: 'select', optionsKey: 'situation' },
  // Teacher/Professor fields
  statutProfesseur: { type: 'select', optionsKey: 'statutProfesseur' },
  specialite: { type: 'text' },
  matieresEnseignees: { type: 'text' },
  tauxHoraire: { type: 'number' },
  estOriginaireEtablissementPublic: { type: 'select', optionsKey: 'ouiNon' },
  etablissementOrigine: { type: 'text' },
  // Staff fields
  fonction: { type: 'text' },
  dateRecrutement: { type: 'date' },
  rib: { type: 'text' },
  salaireMensuelNet: { type: 'number' },
  // Parent fields
  relationAvecEtudiant: { type: 'select', optionsKey: 'relation' },
  relationPrecision: { type: 'text' },
};

interface FieldDisplayProps {
  label: string;
  value?: string | number | null;
  fieldName?: string;
  onMarkForDeletion?: (fieldName: string) => void;
  onUnmarkForDeletion?: (fieldName: string) => void;
  onEdit?: (fieldName: string, value: string) => void;
  isMarkedForDeletion?: boolean;
  isModified?: boolean;
  modifiedValue?: string;
  canClear?: boolean;
  canEdit?: boolean;
  fieldConfig?: FieldConfig;
  nomenclatureData?: NomenclatureData;
}

interface NomenclatureData {
  countries: NomenclatureCountry[];
  handicaps: NomenclatureHandicap[];
  seriesBac: NomenclatureSerieBac[];
  provinces: NomenclatureProvince[];
  diplomes: NomenclatureDiplome[];
}

const FieldDisplay: React.FC<FieldDisplayProps> = ({
  label,
  value,
  fieldName,
  onMarkForDeletion,
  onUnmarkForDeletion,
  onEdit,
  isMarkedForDeletion,
  isModified,
  modifiedValue,
  canClear = true,
  canEdit = true,
  fieldConfig,
  nomenclatureData,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [showArabicKeyboard, setShowArabicKeyboard] = useState(false);
  const arabicInputRef = useRef<HTMLDivElement>(null);

  const displayValue = isModified ? modifiedValue : value;
  const hasValue = displayValue !== null && displayValue !== undefined && displayValue !== '';

  const handleStartEdit = () => {
    // A browser date input only accepts YYYY-MM-DD; stored or displayed dates may be DD/MM/YYYY
    const raw = String(displayValue || '');
    setEditValue(fieldConfig?.type === 'date' ? toIsoDate(raw) || raw : raw);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    if (fieldName && onEdit) {
      onEdit(fieldName, editValue);
    }
    setIsEditing(false);
    setShowArabicKeyboard(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValue('');
    setShowArabicKeyboard(false);
  };

  const handleArabicKeyPress = (char: string) => {
    setEditValue(prev => prev + char);
  };

  const handleArabicBackspace = () => {
    setEditValue(prev => prev.slice(0, -1));
  };

  const getOptions = (): { value: string; label: string }[] => {
    if (!fieldConfig?.optionsKey || !nomenclatureData) return [];

    switch (fieldConfig.optionsKey) {
      case 'countries':
        return nomenclatureData.countries.map(c => ({ value: c.code, label: c.nameFr }));
      case 'handicaps':
        return nomenclatureData.handicaps.map(h => ({ value: h.code, label: h.labelFr }));
      case 'seriesBac':
        return nomenclatureData.seriesBac.map(s => {
          const isEtranger = s.type?.toLowerCase().includes('etranger') || s.type?.toLowerCase().includes('étranger');
          return { value: s.code, label: `${isEtranger ? 'Étranger' : 'Marocain'} - ${s.labelFr} (${s.code})` };
        });
      case 'provinces':
        return nomenclatureData.provinces.map(p => ({ value: p.nameFr, label: p.nameFr }));
      case 'diplomes':
        return nomenclatureData.diplomes.map(d => ({ value: d.labelFr, label: d.labelFr }));
      case 'sexe':
        return SEXE_OPTIONS;
      case 'ouiNon':
        return Object.entries(OUI_NON_LABELS).map(([value, label]) => ({ value, label }));
      case 'situation':
        return Object.entries(SITUATION_LABELS).map(([value, label]) => ({ value, label }));
      case 'mention':
        return Object.entries(MENTION_LABELS).map(([value, label]) => ({ value, label }));
      case 'years':
        return generateYears().map(y => ({ value: y, label: y }));
      case 'relation':
        return Object.entries(RELATION_LABELS).map(([value, label]) => ({ value, label }));
      case 'emergencyRelation':
        return emergencyRelationSelectOptions(editValue).filter(o => o.value !== '');
      case 'statutProfesseur':
        return STATUT_PROFESSEUR_OPTIONS;
      case 'phoneCountry':
        return COUNTRY_PHONE_CODES.map(c => ({ value: c.code, label: `${c.flag} ${c.name} (${c.dialCode})` }));
      default:
        return [];
    }
  };

  // Format the display value for select fields to show French labels
  const getFormattedDisplayValue = (val: unknown): string => {
    if (val === null || val === undefined || val === '') return '';
    const strVal = String(val);

    if (fieldConfig?.optionsKey) {
      const options = getOptions();
      const option = options.find(opt => opt.value === strVal);
      if (option) return option.label;
    }

    // Fallback: check if value exists in emergency relation labels directly
    if (fieldName === 'urgenceRelation') {
      return emergencyRelationLabel(strVal);
    }

    return strVal;
  };

  const renderEditInput = () => {
    if (!fieldConfig) return null;

    const options = getOptions();

    switch (fieldConfig.type) {
      case 'select':
        return (
          <select
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          >
            <option value="">Sélectionner...</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      case 'date':
        return (
          <input
            type="date"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            min="0"
            max="100"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          />
        );
      case 'arabic':
        return (
          <div ref={arabicInputRef}>
            <div className="flex gap-2">
              <input
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                dir="rtl"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm text-right"
                placeholder="اكتب هنا..."
              />
              <button
                type="button"
                onClick={() => setShowArabicKeyboard(!showArabicKeyboard)}
                className={`px-3 py-2 rounded-lg border transition-colors ${
                  showArabicKeyboard
                    ? 'bg-primary-100 border-primary-500 text-primary-700'
                    : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200'
                }`}
                title="Clavier arabe"
              >
                <Keyboard size={18} />
              </button>
            </div>
            {showArabicKeyboard && (
              <VirtualArabicKeyboard
                onKeyPress={handleArabicKeyPress}
                onBackspace={handleArabicBackspace}
                onClose={() => setShowArabicKeyboard(false)}
                anchorRef={arabicInputRef}
              />
            )}
          </div>
        );
      case 'email':
        return (
          <input
            type="email"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            placeholder="exemple@email.com"
          />
        );
      case 'phone':
        return (
          <input
            type="tel"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            placeholder="6XX XXX XXX"
          />
        );
      default:
        return (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          />
        );
    }
  };

  if (isEditing) {
    return (
      <div className="relative p-3 rounded-lg border border-primary-300 bg-primary-50">
        <dt className="text-sm text-gray-500 mb-2">{label}</dt>
        <div className="space-y-2">
          {renderEditInput()}
          <div className="flex gap-2">
            <button
              onClick={handleSaveEdit}
              className="flex-1 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
            >
              Enregistrer
            </button>
            <button
              onClick={handleCancelEdit}
              className="flex-1 px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative p-3 rounded-lg border transition-all ${
        isMarkedForDeletion
          ? 'bg-red-50 border-red-300'
          : isModified
            ? 'bg-blue-50 border-blue-300'
            : hasValue
              ? 'bg-white border-gray-200'
              : 'bg-yellow-50 border-yellow-200'
      }`}
    >
      <div className="pr-16">
        <dt className="text-sm text-gray-500 mb-1">{label}</dt>
        <dd className={`text-sm font-medium break-words ${
          isMarkedForDeletion
            ? 'text-red-500 line-through'
            : isModified
              ? 'text-blue-700'
              : hasValue
                ? 'text-gray-900'
                : 'text-yellow-600 italic'
        }`}>
          {isMarkedForDeletion ? 'Effacé' : hasValue ? getFormattedDisplayValue(displayValue) : '—'}
          {isModified && <span className="ml-2 text-xs text-blue-500">(modifié)</span>}
        </dd>
      </div>

      {/* Action buttons */}
      <div className="absolute top-2 right-2 flex gap-1">
        {/* Edit button */}
        {fieldName && onEdit && canEdit && !isMarkedForDeletion && fieldConfig && (
          <button
            onClick={handleStartEdit}
            className="p-1.5 text-blue-500 hover:bg-blue-100 rounded transition-colors"
            title="Modifier ce champ"
          >
            <Pencil size={14} />
          </button>
        )}

        {/* Delete/Restore button */}
        {fieldName && hasValue && canClear && (
          isMarkedForDeletion ? (
            <button
              onClick={() => onUnmarkForDeletion?.(fieldName)}
              className="p-1.5 text-green-500 hover:bg-green-100 rounded transition-colors"
              title="Annuler l'effacement"
            >
              <RotateCcw size={14} />
            </button>
          ) : (
            <button
              onClick={() => onMarkForDeletion?.(fieldName)}
              className="p-1.5 text-red-500 hover:bg-red-100 rounded transition-colors"
              title="Marquer pour effacement"
            >
              <Trash2 size={14} />
            </button>
          )
        )}
      </div>
    </div>
  );
};

// Document Viewer Modal Component
interface DocumentViewerProps {
  isOpen: boolean;
  onClose: () => void;
  documentUrl: string;
  documentName: string;
  documentType: string;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  isOpen,
  onClose,
  documentUrl,
  documentName,
  documentType,
}) => {
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  // Fetch the file as a same-origin blob so the API's X-Frame-Options can't
  // block the preview ("refused to connect"). Sends the auth header properly.
  const { blobUrl, loading, error } = useAuthedFileBlob(documentUrl, isOpen);

  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(documentName);
  const isPdf = /\.pdf$/i.test(documentName);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 25, 200));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 25, 50));
  const handleRotate = () => setRotation(prev => (prev + 90) % 360);

  useEffect(() => {
    if (isOpen) {
      setZoom(100);
      setRotation(0);
    }
  }, [isOpen, documentUrl]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="fixed inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden z-10 mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <FileText size={20} className="text-gray-500" />
            <div>
              <h3 className="font-medium text-gray-900">{documentType}</h3>
              <p className="text-xs text-gray-500">{documentName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isImage && (
              <>
                <button
                  onClick={handleZoomOut}
                  className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Zoom arrière"
                >
                  <ZoomOut size={18} />
                </button>
                <span className="text-sm text-gray-600 min-w-[50px] text-center">{zoom}%</span>
                <button
                  onClick={handleZoomIn}
                  className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Zoom avant"
                >
                  <ZoomIn size={18} />
                </button>
                <button
                  onClick={handleRotate}
                  className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Rotation"
                >
                  <RotateCw size={18} />
                </button>
                <div className="w-px h-6 bg-gray-300 mx-1" />
              </>
            )}
            <a
              href={blobUrl ?? undefined}
              download={documentName}
              className={`p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors ${blobUrl ? '' : 'pointer-events-none opacity-40'}`}
              title="Télécharger"
            >
              <Download size={18} />
            </a>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-auto max-h-[calc(90vh-60px)] bg-gray-100 flex items-center justify-center p-4 min-h-[400px] relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          )}
          {!loading && error ? (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-4">Impossible d'afficher ce document.</p>
              <a
                href={documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                <ExternalLink size={16} />
                Ouvrir dans un nouvel onglet
              </a>
            </div>
          ) : isImage ? (
            <img
              src={blobUrl ?? undefined}
              alt={documentName}
              style={{
                transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
                transition: 'transform 0.2s ease',
              }}
              className="max-w-full h-auto"
            />
          ) : isPdf ? (
            <iframe
              src={blobUrl ?? undefined}
              title={documentName}
              className="w-full h-[70vh] border-0"
            />
          ) : (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 mb-4">Aperçu non disponible pour ce type de fichier</p>
              <a
                href={blobUrl ?? undefined}
                download={documentName}
                className={`inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 ${blobUrl ? '' : 'pointer-events-none opacity-40'}`}
              >
                <Download size={16} />
                Télécharger le fichier
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ProfileValidationModal: React.FC<ProfileValidationModalProps> = ({
  isOpen,
  onClose,
  validationRequestId,
  onValidationComplete,
}) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationData, setValidationData] = useState<ValidationRequestDetails | null>(null);

  // Nomenclature data for dropdowns
  const [nomenclatureData, setNomenclatureData] = useState<NomenclatureData>({
    countries: [],
    handicaps: [],
    seriesBac: [],
    provinces: [],
    diplomes: [],
  });

  // Track fields marked for deletion (not yet saved)
  const [fieldsMarkedForDeletion, setFieldsMarkedForDeletion] = useState<string[]>([]);
  const [documentsMarkedForDeletion, setDocumentsMarkedForDeletion] = useState<string[]>([]);

  // Track modified fields (not yet saved)
  const [modifiedFields, setModifiedFields] = useState<Record<string, string>>({});

  // Review form state
  const [decision, setDecision] = useState<'APPROVED' | 'REJECTED' | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Document viewer state
  const [viewingDocument, setViewingDocument] = useState<{
    url: string;
    name: string;
    type: string;
  } | null>(null);

  useEffect(() => {
    if (isOpen && validationRequestId) {
      loadValidationRequest();
      loadNomenclatureData();
      // Reset state
      setFieldsMarkedForDeletion([]);
      setDocumentsMarkedForDeletion([]);
      setModifiedFields({});
      setDecision(null);
      setRejectionReason('');
      setViewingDocument(null);
    }
  }, [isOpen, validationRequestId]);

  const loadValidationRequest = async () => {
    console.log('[ProfileValidationModal] Loading validation request:', validationRequestId);
    setLoading(true);
    setError(null);
    try {
      const data = await getValidationRequest(validationRequestId);
      console.log('[ProfileValidationModal] Validation data loaded:', data);
      console.log('[ProfileValidationModal] userProfile:', data.userProfile);
      console.log('[ProfileValidationModal] documents from userProfile:', data.userProfile?.documents);

      // If documents are not included in userProfile, fetch them separately
      if (data.userProfile && (!data.userProfile.documents || data.userProfile.documents.length === 0)) {
        console.log('[ProfileValidationModal] Fetching documents separately for user:', data.userId);
        const documents = await getAdminUserDocuments(data.userId);
        console.log('[ProfileValidationModal] Documents fetched separately:', documents);
        if (documents && documents.length > 0) {
          data.userProfile.documents = documents;
        }
      }

      setValidationData(data);
    } catch (err) {
      console.error('[ProfileValidationModal] Error loading validation request:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadNomenclatureData = async () => {
    try {
      const [countries, handicaps, seriesBac, provinces, diplomes] = await Promise.all([
        fetchCountries(),
        fetchHandicaps(),
        fetchSeriesBac(),
        fetchProvinces(),
        fetchDiplomes(),
      ]);
      setNomenclatureData({ countries, handicaps, seriesBac, provinces, diplomes });
    } catch (err) {
      console.error('Error loading nomenclature data:', err);
    }
  };

  const getSerieBacLabel = (code: string | undefined | null): string => {
    if (!code) return '—';
    const serie = nomenclatureData.seriesBac.find(s => s.code === code);
    return serie ? serie.labelFr : code;
  };

  const getCountryLabel = (code: string | undefined | null): string => {
    if (!code) return '—';
    const country = nomenclatureData.countries.find(c => c.code === code);
    return country ? country.nameFr : code;
  };

  const getHandicapLabel = (code: string | undefined | null): string => {
    if (!code) return null as unknown as string;
    return HANDICAP_LABELS[code] || code;
  };

  const handleMarkFieldForDeletion = (fieldName: string) => {
    setFieldsMarkedForDeletion(prev => [...prev, fieldName]);
    // Remove from modified if it was modified
    setModifiedFields(prev => {
      const newModified = { ...prev };
      delete newModified[fieldName];
      return newModified;
    });
  };

  const handleUnmarkFieldForDeletion = (fieldName: string) => {
    setFieldsMarkedForDeletion(prev => prev.filter(f => f !== fieldName));
  };

  const handleMarkDocumentForDeletion = (documentId: string) => {
    setDocumentsMarkedForDeletion(prev => [...prev, documentId]);
  };

  const handleUnmarkDocumentForDeletion = (documentId: string) => {
    setDocumentsMarkedForDeletion(prev => prev.filter(d => d !== documentId));
  };

  const handleEditField = (fieldName: string, value: string) => {
    setModifiedFields(prev => ({ ...prev, [fieldName]: value }));
    // Remove from deletion list if it was marked
    setFieldsMarkedForDeletion(prev => prev.filter(f => f !== fieldName));
  };

  const handleSubmitReview = async () => {
    if (!decision) return;

    setSubmitting(true);
    setError(null);

    try {
      // Apply all modifications (field updates) - for both APPROVED and REJECTED
      if (Object.keys(modifiedFields).length > 0) {
        for (const [fieldName, value] of Object.entries(modifiedFields)) {
          await updateProfileField(validationRequestId, fieldName, value);
        }
      }

      // Apply all deletions (clear fields and delete documents) - for both APPROVED and REJECTED
      // Clear marked fields
      for (const fieldName of fieldsMarkedForDeletion) {
        await clearProfileField(validationRequestId, fieldName);
      }
      // Delete marked documents
      for (const docId of documentsMarkedForDeletion) {
        await deleteValidationDocument(validationRequestId, docId);
      }

      // Submit the review
      await reviewValidation(validationRequestId, {
        decision,
        rejectionReason: decision === 'REJECTED' ? (rejectionReason || 'Champs à corriger') : undefined,
        rejectionFields: decision === 'REJECTED' && fieldsMarkedForDeletion.length > 0 ? fieldsMarkedForDeletion : undefined,
      });

      onValidationComplete();
      onClose();
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors de la validation');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    return toDisplayDate(dateStr) || dateStr;
  };

  const formatCurrency = (value?: string | null) => {
    if (!value) return '—';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }).format(num);
  };

  const openDocumentViewer = (doc: { fileUrl: string; fileName: string; documentType: string }) => {
    setViewingDocument({
      url: `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}${doc.fileUrl}`,
      name: doc.fileName,
      type: doc.documentType,
    });
  };

  if (!isOpen) return null;

  const userRole = validationData?.user?.role;
  const isStudent = userRole === 'STUDENT';
  const isPureTeacher = userRole === 'TEACHER'; // Only TEACHER role (not staff who also teach)
  const isAlsoProfessor = validationData?.user?.isAlsoProfessor === true;
  const isTeacher = isPureTeacher || isAlsoProfessor; // Shows professor section for both
  const isStaff = !isStudent && userRole !== 'PARENT' && userRole !== 'TEACHER'; // Staff roles (even if they also teach)
  const isParent = userRole === 'PARENT';

  // Check if any modifications were made
  const hasDeletions = fieldsMarkedForDeletion.length > 0 || documentsMarkedForDeletion.length > 0;
  const hasModifications = Object.keys(modifiedFields).length > 0;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[95vh] overflow-hidden z-10">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-primary-600 to-primary-700">
            <div>
              <h2 className="text-lg font-semibold text-white">
                Validation du profil
              </h2>
              {validationData && (
                <p className="text-primary-100 text-sm mt-0.5">
                  {validationData.user.firstName} {validationData.user.lastName} - {ROLE_LABELS[validationData.user.role] || validationData.user.role}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(95vh-180px)]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
              </div>
            ) : error && !validationData ? (
              <div className="flex flex-col items-center justify-center py-12">
                <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
                <p className="text-red-600">{error}</p>
                {error.includes('propre profil') ? (
                  <button
                    onClick={onClose}
                    className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Fermer
                  </button>
                ) : (
                  <button
                    onClick={loadValidationRequest}
                    className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Réessayer
                  </button>
                )}
              </div>
            ) : validationData ? (
              <div className="p-6 space-y-4">
                {/* Info banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Comment valider un profil:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li><Pencil size={12} className="inline" /> Cliquez sur le crayon pour <strong>modifier</strong> une valeur</li>
                      <li><Trash2 size={12} className="inline" /> Cliquez sur la poubelle pour <strong>effacer</strong> un champ incorrect</li>
                      <li><strong>Approuver:</strong> Valide le profil (avec les modifications si effectuées)</li>
                      <li><strong>Rejeter:</strong> Applique les effacements et demande des corrections à l'utilisateur</li>
                    </ul>
                  </div>
                </div>

                {/* Warning if fields marked for deletion */}
                {hasDeletions && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-yellow-800">Champs marqués pour effacement</p>
                      <p className="text-sm text-yellow-700 mt-1">
                        {fieldsMarkedForDeletion.length} champ(s) et {documentsMarkedForDeletion.length} document(s) seront effacés si vous cliquez sur "Rejeter".
                      </p>
                    </div>
                  </div>
                )}

                {/* Info if fields modified */}
                {hasModifications && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                    <Pencil className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800">Champs modifiés</p>
                      <p className="text-sm text-blue-700 mt-1">
                        {Object.keys(modifiedFields).length} champ(s) modifié(s). Ces modifications seront appliquées si vous cliquez sur "Approuver".
                      </p>
                    </div>
                  </div>
                )}

                {/* Section 1: Identité */}
                <CollapsibleSection
                  title="Section 1: Identité"
                  icon={<User size={18} className="text-primary-600" />}
                  defaultOpen={true}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <FieldDisplay
                      label="Nom"
                      value={validationData.user.lastName}
                      fieldName="nom"
                      onMarkForDeletion={handleMarkFieldForDeletion}
                      onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                      onEdit={handleEditField}
                      isMarkedForDeletion={fieldsMarkedForDeletion.includes('nom')}
                      isModified={!!modifiedFields['nom']}
                      modifiedValue={modifiedFields['nom']}
                      fieldConfig={FIELD_CONFIGS['nom']}
                      nomenclatureData={nomenclatureData}
                    />
                    <FieldDisplay
                      label="Prénom"
                      value={validationData.user.firstName}
                      fieldName="prenom"
                      onMarkForDeletion={handleMarkFieldForDeletion}
                      onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                      onEdit={handleEditField}
                      isMarkedForDeletion={fieldsMarkedForDeletion.includes('prenom')}
                      isModified={!!modifiedFields['prenom']}
                      modifiedValue={modifiedFields['prenom']}
                      fieldConfig={FIELD_CONFIGS['prenom']}
                      nomenclatureData={nomenclatureData}
                    />
                    <FieldDisplay
                      label="Nom (Arabe)"
                      value={validationData.userProfile?.nomArabe}
                      fieldName="nomArabe"
                      onMarkForDeletion={handleMarkFieldForDeletion}
                      onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                      onEdit={handleEditField}
                      isMarkedForDeletion={fieldsMarkedForDeletion.includes('nomArabe')}
                      isModified={!!modifiedFields['nomArabe']}
                      modifiedValue={modifiedFields['nomArabe']}
                      fieldConfig={FIELD_CONFIGS['nomArabe']}
                      nomenclatureData={nomenclatureData}
                    />
                    <FieldDisplay
                      label="Prénom (Arabe)"
                      value={validationData.userProfile?.prenomArabe}
                      fieldName="prenomArabe"
                      onMarkForDeletion={handleMarkFieldForDeletion}
                      onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                      onEdit={handleEditField}
                      isMarkedForDeletion={fieldsMarkedForDeletion.includes('prenomArabe')}
                      isModified={!!modifiedFields['prenomArabe']}
                      modifiedValue={modifiedFields['prenomArabe']}
                      fieldConfig={FIELD_CONFIGS['prenomArabe']}
                      nomenclatureData={nomenclatureData}
                    />
                    <FieldDisplay
                      label="CNIE"
                      value={validationData.userProfile?.cnie}
                      fieldName="cnie"
                      onMarkForDeletion={handleMarkFieldForDeletion}
                      onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                      onEdit={handleEditField}
                      isMarkedForDeletion={fieldsMarkedForDeletion.includes('cnie')}
                      isModified={!!modifiedFields['cnie']}
                      modifiedValue={modifiedFields['cnie']}
                      fieldConfig={FIELD_CONFIGS['cnie']}
                      nomenclatureData={nomenclatureData}
                    />
                    <FieldDisplay
                      label="Passeport"
                      value={validationData.userProfile?.passport}
                      fieldName="passport"
                      onMarkForDeletion={handleMarkFieldForDeletion}
                      onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                      onEdit={handleEditField}
                      isMarkedForDeletion={fieldsMarkedForDeletion.includes('passport')}
                      isModified={!!modifiedFields['passport']}
                      modifiedValue={modifiedFields['passport']}
                      fieldConfig={FIELD_CONFIGS['passport']}
                      nomenclatureData={nomenclatureData}
                    />
                    <FieldDisplay
                      label="Sexe"
                      value={validationData.userProfile?.sexe === 'M' ? 'Masculin' : validationData.userProfile?.sexe === 'F' ? 'Féminin' : validationData.userProfile?.sexe}
                      fieldName="sexe"
                      onMarkForDeletion={handleMarkFieldForDeletion}
                      onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                      onEdit={handleEditField}
                      isMarkedForDeletion={fieldsMarkedForDeletion.includes('sexe')}
                      isModified={!!modifiedFields['sexe']}
                      modifiedValue={modifiedFields['sexe'] === 'M' ? 'Masculin' : modifiedFields['sexe'] === 'F' ? 'Féminin' : modifiedFields['sexe']}
                      fieldConfig={FIELD_CONFIGS['sexe']}
                      nomenclatureData={nomenclatureData}
                    />
                    <FieldDisplay
                      label="Date de naissance"
                      value={formatDate(validationData.userProfile?.dateNaissance)}
                      fieldName="dateNaissance"
                      onMarkForDeletion={handleMarkFieldForDeletion}
                      onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                      onEdit={handleEditField}
                      isMarkedForDeletion={fieldsMarkedForDeletion.includes('dateNaissance')}
                      isModified={!!modifiedFields['dateNaissance']}
                      modifiedValue={modifiedFields['dateNaissance']}
                      fieldConfig={FIELD_CONFIGS['dateNaissance']}
                      nomenclatureData={nomenclatureData}
                    />
                    <FieldDisplay
                      label="Pays d'origine"
                      value={getCountryLabel(validationData.userProfile?.paysOrigine)}
                      fieldName="paysOrigine"
                      onMarkForDeletion={handleMarkFieldForDeletion}
                      onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                      onEdit={handleEditField}
                      isMarkedForDeletion={fieldsMarkedForDeletion.includes('paysOrigine')}
                      isModified={!!modifiedFields['paysOrigine']}
                      modifiedValue={getCountryLabel(modifiedFields['paysOrigine'])}
                      fieldConfig={FIELD_CONFIGS['paysOrigine']}
                      nomenclatureData={nomenclatureData}
                    />
                    <FieldDisplay
                      label="Situation de handicap"
                      value={getHandicapLabel(validationData.userProfile?.handicap)}
                      fieldName="handicap"
                      onMarkForDeletion={handleMarkFieldForDeletion}
                      onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                      onEdit={handleEditField}
                      isMarkedForDeletion={fieldsMarkedForDeletion.includes('handicap')}
                      isModified={!!modifiedFields['handicap']}
                      modifiedValue={getHandicapLabel(modifiedFields['handicap'])}
                      fieldConfig={FIELD_CONFIGS['handicap']}
                      nomenclatureData={nomenclatureData}
                    />
                  </div>
                </CollapsibleSection>

                {/* Section 2: Coordonnées */}
                <CollapsibleSection
                  title="Section 2: Coordonnées"
                  icon={<Phone size={18} className="text-primary-600" />}
                  defaultOpen={true}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FieldDisplay
                      label="Email"
                      value={modifiedFields['email'] || validationData.user.email}
                      fieldName="email"
                      onMarkForDeletion={handleMarkFieldForDeletion}
                      onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                      onEdit={handleEditField}
                      isMarkedForDeletion={fieldsMarkedForDeletion.includes('email')}
                      isModified={!!modifiedFields['email']}
                      modifiedValue={modifiedFields['email']}
                      fieldConfig={FIELD_CONFIGS['email']}
                      nomenclatureData={nomenclatureData}
                    />
                    <div className="space-y-2">
                      <FieldDisplay
                        label="Indicatif téléphone"
                        value={(() => {
                          const countryCode = modifiedFields['phoneCountry'] || validationData.userProfile?.phoneCountry || 'MA';
                          const country = COUNTRY_PHONE_CODES.find(c => c.code === countryCode);
                          return country ? `${country.flag} ${country.name} (${country.dialCode})` : countryCode;
                        })()}
                        fieldName="phoneCountry"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        onEdit={handleEditField}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('phoneCountry')}
                        isModified={!!modifiedFields['phoneCountry']}
                        modifiedValue={(() => {
                          const country = COUNTRY_PHONE_CODES.find(c => c.code === modifiedFields['phoneCountry']);
                          return country ? `${country.flag} ${country.name} (${country.dialCode})` : modifiedFields['phoneCountry'];
                        })()}
                        fieldConfig={FIELD_CONFIGS['phoneCountry']}
                        nomenclatureData={nomenclatureData}
                      />
                      <FieldDisplay
                        label="Numéro de téléphone"
                        value={modifiedFields['telephone'] || validationData.user.phone}
                        fieldName="telephone"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        onEdit={handleEditField}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('telephone')}
                        isModified={!!modifiedFields['telephone']}
                        modifiedValue={modifiedFields['telephone']}
                        fieldConfig={FIELD_CONFIGS['telephone']}
                        nomenclatureData={nomenclatureData}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <FieldDisplay
                        label="Adresse"
                        value={validationData.userProfile?.adresse}
                        fieldName="adresse"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        onEdit={handleEditField}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('adresse')}
                        isModified={!!modifiedFields['adresse']}
                        modifiedValue={modifiedFields['adresse']}
                        fieldConfig={FIELD_CONFIGS['adresse']}
                        nomenclatureData={nomenclatureData}
                      />
                    </div>
                  </div>
                </CollapsibleSection>

                {/* Section 3: Contact d'Urgence */}
                <CollapsibleSection
                  title="Section 3: Contact d'Urgence"
                  icon={<Users size={18} className="text-primary-600" />}
                  defaultOpen={true}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <FieldDisplay
                      label="Nom du contact"
                      value={validationData.userProfile?.urgenceNom}
                      fieldName="urgenceNom"
                      onMarkForDeletion={handleMarkFieldForDeletion}
                      onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                      onEdit={handleEditField}
                      isMarkedForDeletion={fieldsMarkedForDeletion.includes('urgenceNom')}
                      isModified={!!modifiedFields['urgenceNom']}
                      modifiedValue={modifiedFields['urgenceNom']}
                      fieldConfig={FIELD_CONFIGS['urgenceNom']}
                      nomenclatureData={nomenclatureData}
                    />
                    <FieldDisplay
                      label="Relation"
                      value={validationData.userProfile?.urgenceRelation}
                      fieldName="urgenceRelation"
                      onMarkForDeletion={handleMarkFieldForDeletion}
                      onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                      onEdit={handleEditField}
                      isMarkedForDeletion={fieldsMarkedForDeletion.includes('urgenceRelation')}
                      isModified={!!modifiedFields['urgenceRelation']}
                      modifiedValue={modifiedFields['urgenceRelation']}
                      fieldConfig={FIELD_CONFIGS['urgenceRelation']}
                      nomenclatureData={nomenclatureData}
                    />
                    <div className="space-y-2">
                      <FieldDisplay
                        label="Indicatif téléphone"
                        value={(() => {
                          const countryCode = modifiedFields['urgencePhoneCountry'] || validationData.userProfile?.urgencePhoneCountry || 'MA';
                          const country = COUNTRY_PHONE_CODES.find(c => c.code === countryCode);
                          return country ? `${country.flag} ${country.name} (${country.dialCode})` : countryCode;
                        })()}
                        fieldName="urgencePhoneCountry"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        onEdit={handleEditField}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('urgencePhoneCountry')}
                        isModified={!!modifiedFields['urgencePhoneCountry']}
                        modifiedValue={(() => {
                          const country = COUNTRY_PHONE_CODES.find(c => c.code === modifiedFields['urgencePhoneCountry']);
                          return country ? `${country.flag} ${country.name} (${country.dialCode})` : modifiedFields['urgencePhoneCountry'];
                        })()}
                        fieldConfig={FIELD_CONFIGS['urgencePhoneCountry']}
                        nomenclatureData={nomenclatureData}
                      />
                      <FieldDisplay
                        label="Numéro de téléphone"
                        value={modifiedFields['urgenceNumero'] || validationData.userProfile?.urgenceNumero}
                        fieldName="urgenceNumero"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        onEdit={handleEditField}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('urgenceNumero')}
                        isModified={!!modifiedFields['urgenceNumero']}
                        modifiedValue={modifiedFields['urgenceNumero']}
                        fieldConfig={FIELD_CONFIGS['urgenceNumero']}
                        nomenclatureData={nomenclatureData}
                      />
                    </div>
                    <div className="md:col-span-2 lg:col-span-3">
                      <FieldDisplay
                        label="Adresse"
                        value={validationData.userProfile?.urgenceAdresse}
                        fieldName="urgenceAdresse"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        onEdit={handleEditField}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('urgenceAdresse')}
                        isModified={!!modifiedFields['urgenceAdresse']}
                        modifiedValue={modifiedFields['urgenceAdresse']}
                        fieldConfig={FIELD_CONFIGS['urgenceAdresse']}
                        nomenclatureData={nomenclatureData}
                      />
                    </div>
                  </div>
                </CollapsibleSection>

                {/* Student Sections */}
                {isStudent && validationData.studentData && (
                  <>
                    {/* Section 4: Inscription Académique */}
                    <CollapsibleSection
                      title="Section 4: Inscription Académique"
                      icon={<GraduationCap size={18} className="text-primary-600" />}
                      defaultOpen={true}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <FieldDisplay
                          label="N° Massar/CNE"
                          value={validationData.studentData.numeroMassarCNE}
                          fieldName="numeroMassarCNE"
                          onMarkForDeletion={handleMarkFieldForDeletion}
                          onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                          onEdit={handleEditField}
                          isMarkedForDeletion={fieldsMarkedForDeletion.includes('numeroMassarCNE')}
                          isModified={!!modifiedFields['numeroMassarCNE']}
                          modifiedValue={modifiedFields['numeroMassarCNE']}
                          fieldConfig={FIELD_CONFIGS['numeroMassarCNE']}
                          nomenclatureData={nomenclatureData}
                        />
                        <FieldDisplay
                          label="Diplôme préparé"
                          value={validationData.studentData.diplomeInscrit}
                          canClear={false}
                          canEdit={false}
                        />
                        <FieldDisplay
                          label="Filière"
                          value={validationData.studentData.filiereInscrit}
                          canClear={false}
                          canEdit={false}
                        />
                        <FieldDisplay
                          label="Niveau"
                          value={validationData.studentData.niveauFormation ? `${validationData.studentData.niveauFormation}ème année` : null}
                          canClear={false}
                          canEdit={false}
                        />
                        <FieldDisplay
                          label="Situation"
                          value={SITUATION_LABELS[validationData.studentData.situation || ''] || validationData.studentData.situation}
                          canClear={false}
                          canEdit={false}
                        />
                        <FieldDisplay
                          label="Première année d'inscription"
                          value={validationData.studentData.premiereAnneeInscription}
                          canClear={false}
                          canEdit={false}
                        />
                        {validationData.studentData.campus && (
                          <FieldDisplay
                            label="Campus"
                            value={`${validationData.studentData.campus.name} - ${validationData.studentData.campus.city}`}
                            canClear={false}
                            canEdit={false}
                          />
                        )}
                      </div>
                    </CollapsibleSection>

                    {/* Section 5: Diplôme d'Accès */}
                    <CollapsibleSection
                      title="Section 5: Diplôme d'Accès"
                      icon={<Award size={18} className="text-primary-600" />}
                      defaultOpen={true}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <FieldDisplay
                          label="Série du Baccalauréat"
                          value={getSerieBacLabel(modifiedFields['serieBaccalaureat'] || validationData.studentData.serieBaccalaureat)}
                          fieldName="serieBaccalaureat"
                          onMarkForDeletion={handleMarkFieldForDeletion}
                          onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                          onEdit={handleEditField}
                          isMarkedForDeletion={fieldsMarkedForDeletion.includes('serieBaccalaureat')}
                          isModified={!!modifiedFields['serieBaccalaureat']}
                          modifiedValue={getSerieBacLabel(modifiedFields['serieBaccalaureat'])}
                          fieldConfig={FIELD_CONFIGS['serieBaccalaureat']}
                          nomenclatureData={nomenclatureData}
                        />
                        <FieldDisplay
                          label="Année d'obtention du Bac"
                          value={validationData.studentData.anneeObtentionBac}
                          fieldName="anneeObtentionBac"
                          onMarkForDeletion={handleMarkFieldForDeletion}
                          onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                          onEdit={handleEditField}
                          isMarkedForDeletion={fieldsMarkedForDeletion.includes('anneeObtentionBac')}
                          isModified={!!modifiedFields['anneeObtentionBac']}
                          modifiedValue={modifiedFields['anneeObtentionBac']}
                          fieldConfig={FIELD_CONFIGS['anneeObtentionBac']}
                          nomenclatureData={nomenclatureData}
                        />
                        <FieldDisplay
                          label="Diplôme d'accès"
                          value={validationData.studentData.diplomeAcces}
                          fieldName="diplomeAcces"
                          onMarkForDeletion={handleMarkFieldForDeletion}
                          onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                          onEdit={handleEditField}
                          isMarkedForDeletion={fieldsMarkedForDeletion.includes('diplomeAcces')}
                          isModified={!!modifiedFields['diplomeAcces']}
                          modifiedValue={modifiedFields['diplomeAcces']}
                          fieldConfig={FIELD_CONFIGS['diplomeAcces']}
                          nomenclatureData={nomenclatureData}
                        />
                        <FieldDisplay
                          label="Spécialité du diplôme"
                          value={validationData.studentData.specialiteDiplomeAcces}
                          fieldName="specialiteDiplomeAcces"
                          onMarkForDeletion={handleMarkFieldForDeletion}
                          onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                          onEdit={handleEditField}
                          isMarkedForDeletion={fieldsMarkedForDeletion.includes('specialiteDiplomeAcces')}
                          isModified={!!modifiedFields['specialiteDiplomeAcces']}
                          modifiedValue={modifiedFields['specialiteDiplomeAcces']}
                          fieldConfig={FIELD_CONFIGS['specialiteDiplomeAcces']}
                          nomenclatureData={nomenclatureData}
                        />
                        <FieldDisplay
                          label="Mention"
                          value={MENTION_LABELS[validationData.studentData.mentionDiplomeAcces || ''] || validationData.studentData.mentionDiplomeAcces}
                          fieldName="mentionDiplomeAcces"
                          onMarkForDeletion={handleMarkFieldForDeletion}
                          onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                          onEdit={handleEditField}
                          isMarkedForDeletion={fieldsMarkedForDeletion.includes('mentionDiplomeAcces')}
                          isModified={!!modifiedFields['mentionDiplomeAcces']}
                          modifiedValue={MENTION_LABELS[modifiedFields['mentionDiplomeAcces'] || '']}
                          fieldConfig={FIELD_CONFIGS['mentionDiplomeAcces']}
                          nomenclatureData={nomenclatureData}
                        />
                        <FieldDisplay
                          label="Lieu d'obtention"
                          value={validationData.studentData.lieuObtentionDiplomeAcces}
                          fieldName="lieuObtentionDiplomeAcces"
                          onMarkForDeletion={handleMarkFieldForDeletion}
                          onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                          onEdit={handleEditField}
                          isMarkedForDeletion={fieldsMarkedForDeletion.includes('lieuObtentionDiplomeAcces')}
                          isModified={!!modifiedFields['lieuObtentionDiplomeAcces']}
                          modifiedValue={modifiedFields['lieuObtentionDiplomeAcces']}
                          fieldConfig={FIELD_CONFIGS['lieuObtentionDiplomeAcces']}
                          nomenclatureData={nomenclatureData}
                        />
                      </div>
                    </CollapsibleSection>

                    {/* Section 6: Statut & Aides */}
                    <CollapsibleSection
                      title="Section 6: Statut & Aides"
                      icon={<Briefcase size={18} className="text-primary-600" />}
                      defaultOpen={true}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <FieldDisplay
                          label="Résident"
                          value={OUI_NON_LABELS[validationData.studentData.resident || ''] || validationData.studentData.resident}
                          fieldName="resident"
                          onMarkForDeletion={handleMarkFieldForDeletion}
                          onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                          onEdit={handleEditField}
                          isMarkedForDeletion={fieldsMarkedForDeletion.includes('resident')}
                          isModified={!!modifiedFields['resident']}
                          modifiedValue={OUI_NON_LABELS[modifiedFields['resident'] || '']}
                          fieldConfig={FIELD_CONFIGS['resident']}
                          nomenclatureData={nomenclatureData}
                        />
                        <FieldDisplay
                          label="Fonctionnaire"
                          value={OUI_NON_LABELS[validationData.studentData.fonctionnaire || ''] || validationData.studentData.fonctionnaire}
                          fieldName="fonctionnaire"
                          onMarkForDeletion={handleMarkFieldForDeletion}
                          onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                          onEdit={handleEditField}
                          isMarkedForDeletion={fieldsMarkedForDeletion.includes('fonctionnaire')}
                          isModified={!!modifiedFields['fonctionnaire']}
                          modifiedValue={OUI_NON_LABELS[modifiedFields['fonctionnaire'] || '']}
                          fieldConfig={FIELD_CONFIGS['fonctionnaire']}
                          nomenclatureData={nomenclatureData}
                        />
                        <FieldDisplay
                          label="Pourcentage de bourse"
                          value={validationData.studentData.pourcentageBourse !== null && validationData.studentData.pourcentageBourse !== undefined ? `${validationData.studentData.pourcentageBourse}%` : null}
                          fieldName="pourcentageBourse"
                          onMarkForDeletion={handleMarkFieldForDeletion}
                          onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                          onEdit={handleEditField}
                          isMarkedForDeletion={fieldsMarkedForDeletion.includes('pourcentageBourse')}
                          isModified={!!modifiedFields['pourcentageBourse']}
                          modifiedValue={modifiedFields['pourcentageBourse'] ? `${modifiedFields['pourcentageBourse']}%` : undefined}
                          fieldConfig={FIELD_CONFIGS['pourcentageBourse']}
                          nomenclatureData={nomenclatureData}
                        />
                        <FieldDisplay
                          label="Mobilité"
                          value={OUI_NON_LABELS[validationData.studentData.mobilite || ''] || validationData.studentData.mobilite}
                          fieldName="mobilite"
                          onMarkForDeletion={handleMarkFieldForDeletion}
                          onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                          onEdit={handleEditField}
                          isMarkedForDeletion={fieldsMarkedForDeletion.includes('mobilite')}
                          isModified={!!modifiedFields['mobilite']}
                          modifiedValue={OUI_NON_LABELS[modifiedFields['mobilite'] || '']}
                          fieldConfig={FIELD_CONFIGS['mobilite']}
                          nomenclatureData={nomenclatureData}
                        />
                      </div>
                    </CollapsibleSection>
                  </>
                )}

                {/* Teacher/Professor Section */}
                {isTeacher && (
                  <CollapsibleSection
                    title="Section 4: Informations Professeur"
                    icon={<BookOpen size={18} className="text-purple-600" />}
                    defaultOpen={true}
                    badge="Professeur"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <FieldDisplay
                        label="Statut"
                        value={validationData.userProfile?.statutProfesseur === 'P' ? 'Permanent' : validationData.userProfile?.statutProfesseur === 'V' ? 'Vacataire' : validationData.userProfile?.statutProfesseur}
                        fieldName="statutProfesseur"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        onEdit={handleEditField}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('statutProfesseur')}
                        isModified={!!modifiedFields['statutProfesseur']}
                        modifiedValue={modifiedFields['statutProfesseur'] === 'P' ? 'Permanent' : modifiedFields['statutProfesseur'] === 'V' ? 'Vacataire' : modifiedFields['statutProfesseur']}
                        fieldConfig={FIELD_CONFIGS['statutProfesseur']}
                        nomenclatureData={nomenclatureData}
                      />
                      <FieldDisplay
                        label="Département"
                        value={validationData.nomenclatureData?.department?.labelFr}
                        fieldName="departementAttacheId"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('departementAttacheId')}
                        canEdit={false}
                      />
                      <FieldDisplay
                        label="Grade"
                        value={validationData.nomenclatureData?.grade?.labelFr}
                        fieldName="gradeId"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('gradeId')}
                        canEdit={false}
                      />
                      <FieldDisplay
                        label="Spécialité"
                        value={validationData.userProfile?.specialite}
                        fieldName="specialite"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        onEdit={handleEditField}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('specialite')}
                        isModified={!!modifiedFields['specialite']}
                        modifiedValue={modifiedFields['specialite']}
                        fieldConfig={FIELD_CONFIGS['specialite']}
                        nomenclatureData={nomenclatureData}
                      />
                      <FieldDisplay
                        label="Matières enseignées"
                        value={validationData.userProfile?.matieresEnseignees}
                        fieldName="matieresEnseignees"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        onEdit={handleEditField}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('matieresEnseignees')}
                        isModified={!!modifiedFields['matieresEnseignees']}
                        modifiedValue={modifiedFields['matieresEnseignees']}
                        fieldConfig={FIELD_CONFIGS['matieresEnseignees']}
                        nomenclatureData={nomenclatureData}
                      />
                      {(validationData.userProfile?.statutProfesseur === 'V' || modifiedFields['statutProfesseur'] === 'V') && (
                        <>
                          <FieldDisplay
                            label="Taux horaire"
                            value={validationData.userProfile?.tauxHoraire ? `${validationData.userProfile.tauxHoraire} MAD/h` : null}
                            fieldName="tauxHoraire"
                            onMarkForDeletion={handleMarkFieldForDeletion}
                            onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                            onEdit={handleEditField}
                            isMarkedForDeletion={fieldsMarkedForDeletion.includes('tauxHoraire')}
                            isModified={!!modifiedFields['tauxHoraire']}
                            modifiedValue={modifiedFields['tauxHoraire'] ? `${modifiedFields['tauxHoraire']} MAD/h` : undefined}
                            fieldConfig={FIELD_CONFIGS['tauxHoraire']}
                            nomenclatureData={nomenclatureData}
                          />
                          <FieldDisplay
                            label="Originaire établissement public"
                            value={validationData.userProfile?.estOriginaireEtablissementPublic === 'O' ? 'Oui' : validationData.userProfile?.estOriginaireEtablissementPublic === 'N' ? 'Non' : validationData.userProfile?.estOriginaireEtablissementPublic}
                            fieldName="estOriginaireEtablissementPublic"
                            onMarkForDeletion={handleMarkFieldForDeletion}
                            onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                            onEdit={handleEditField}
                            isMarkedForDeletion={fieldsMarkedForDeletion.includes('estOriginaireEtablissementPublic')}
                            isModified={!!modifiedFields['estOriginaireEtablissementPublic']}
                            modifiedValue={OUI_NON_LABELS[modifiedFields['estOriginaireEtablissementPublic'] || '']}
                            fieldConfig={FIELD_CONFIGS['estOriginaireEtablissementPublic']}
                            nomenclatureData={nomenclatureData}
                          />
                          <FieldDisplay
                            label="Établissement d'origine"
                            value={validationData.userProfile?.etablissementOrigine}
                            fieldName="etablissementOrigine"
                            onMarkForDeletion={handleMarkFieldForDeletion}
                            onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                            onEdit={handleEditField}
                            isMarkedForDeletion={fieldsMarkedForDeletion.includes('etablissementOrigine')}
                            isModified={!!modifiedFields['etablissementOrigine']}
                            modifiedValue={modifiedFields['etablissementOrigine']}
                            fieldConfig={FIELD_CONFIGS['etablissementOrigine']}
                            nomenclatureData={nomenclatureData}
                          />
                        </>
                      )}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Staff Section - Show for all staff roles (even if they also teach) */}
                {isStaff && (
                  <CollapsibleSection
                    title={isAlsoProfessor ? "Section 5: Informations Professionnelles (Staff)" : "Section 4: Informations Professionnelles"}
                    icon={<Briefcase size={18} className="text-green-600" />}
                    defaultOpen={true}
                    badge="Staff"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <FieldDisplay
                        label="Fonction"
                        value={validationData.userProfile?.fonction}
                        fieldName="fonction"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        onEdit={handleEditField}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('fonction')}
                        isModified={!!modifiedFields['fonction']}
                        modifiedValue={modifiedFields['fonction']}
                        fieldConfig={FIELD_CONFIGS['fonction']}
                        nomenclatureData={nomenclatureData}
                      />
                      <FieldDisplay
                        label="Date de recrutement"
                        value={formatDate(validationData.userProfile?.dateRecrutement)}
                        fieldName="dateRecrutement"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        onEdit={handleEditField}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('dateRecrutement')}
                        isModified={!!modifiedFields['dateRecrutement']}
                        modifiedValue={modifiedFields['dateRecrutement']}
                        fieldConfig={FIELD_CONFIGS['dateRecrutement']}
                        nomenclatureData={nomenclatureData}
                      />
                      <FieldDisplay
                        label="Diplôme"
                        value={validationData.nomenclatureData?.diplome?.labelFr}
                        fieldName="diplomeId"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('diplomeId')}
                        canEdit={false}
                      />
                      <FieldDisplay
                        label="RIB"
                        value={validationData.userProfile?.rib}
                        fieldName="rib"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        onEdit={handleEditField}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('rib')}
                        isModified={!!modifiedFields['rib']}
                        modifiedValue={modifiedFields['rib']}
                        fieldConfig={FIELD_CONFIGS['rib']}
                        nomenclatureData={nomenclatureData}
                      />
                      <FieldDisplay
                        label="Salaire mensuel net"
                        value={validationData.userProfile?.salaireMensuelNet ? formatCurrency(validationData.userProfile.salaireMensuelNet) : null}
                        fieldName="salaireMensuelNet"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        onEdit={handleEditField}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('salaireMensuelNet')}
                        isModified={!!modifiedFields['salaireMensuelNet']}
                        modifiedValue={modifiedFields['salaireMensuelNet'] ? formatCurrency(modifiedFields['salaireMensuelNet']) : undefined}
                        fieldConfig={FIELD_CONFIGS['salaireMensuelNet']}
                        nomenclatureData={nomenclatureData}
                      />
                    </div>
                  </CollapsibleSection>
                )}

                {/* Parent Section */}
                {isParent && (
                  <CollapsibleSection
                    title="Section 4: Informations Parent/Tuteur"
                    icon={<Users size={18} className="text-orange-600" />}
                    defaultOpen={true}
                    badge="Parent"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <FieldDisplay
                        label="Relation avec l'étudiant"
                        value={validationData.userProfile?.relationAvecEtudiant ? RELATION_LABELS[validationData.userProfile.relationAvecEtudiant] || validationData.userProfile.relationAvecEtudiant : null}
                        fieldName="relationAvecEtudiant"
                        onMarkForDeletion={handleMarkFieldForDeletion}
                        onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                        onEdit={handleEditField}
                        isMarkedForDeletion={fieldsMarkedForDeletion.includes('relationAvecEtudiant')}
                        isModified={!!modifiedFields['relationAvecEtudiant']}
                        modifiedValue={RELATION_LABELS[modifiedFields['relationAvecEtudiant'] || ''] || modifiedFields['relationAvecEtudiant']}
                        fieldConfig={FIELD_CONFIGS['relationAvecEtudiant']}
                        nomenclatureData={nomenclatureData}
                      />
                      {(validationData.userProfile?.relationPrecision || modifiedFields['relationPrecision']) && (
                        <FieldDisplay
                          label="Précision"
                          value={validationData.userProfile?.relationPrecision}
                          fieldName="relationPrecision"
                          onMarkForDeletion={handleMarkFieldForDeletion}
                          onUnmarkForDeletion={handleUnmarkFieldForDeletion}
                          onEdit={handleEditField}
                          isMarkedForDeletion={fieldsMarkedForDeletion.includes('relationPrecision')}
                          isModified={!!modifiedFields['relationPrecision']}
                          modifiedValue={modifiedFields['relationPrecision']}
                          fieldConfig={FIELD_CONFIGS['relationPrecision']}
                          nomenclatureData={nomenclatureData}
                        />
                      )}
                    </div>
                  </CollapsibleSection>
                )}

                {/* Documents Section - Always show */}
                <CollapsibleSection
                  title="Documents"
                  icon={<FileText size={18} className="text-gray-600" />}
                  defaultOpen={true}
                  badge={validationData.userProfile?.documents ? `${validationData.userProfile.documents.filter(d => !documentsMarkedForDeletion.includes(d.id)).length}` : '0'}
                >
                  {validationData.userProfile?.documents && validationData.userProfile.documents.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {validationData.userProfile.documents.map((doc) => {
                        const isMarkedForDeletion = documentsMarkedForDeletion.includes(doc.id);
                        return (
                          <div
                            key={doc.id}
                            className={`flex items-center justify-between p-3 rounded-lg border ${isMarkedForDeletion ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <FileText size={20} className={isMarkedForDeletion ? 'text-red-400' : 'text-gray-400'} />
                              <div className="min-w-0 flex-1">
                                <p className={`text-sm font-medium ${isMarkedForDeletion ? 'text-red-500 line-through' : 'text-gray-900'}`}>
                                  {doc.documentType}
                                </p>
                                <p className={`text-xs truncate ${isMarkedForDeletion ? 'text-red-400' : 'text-gray-500'}`}>
                                  {doc.fileName}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {doc.isVerified && !isMarkedForDeletion && (
                                <CheckCircle size={16} className="text-green-500" />
                              )}
                              {!isMarkedForDeletion ? (
                                <>
                                  <button
                                    onClick={() => openDocumentViewer(doc)}
                                    className="p-1.5 text-primary-600 hover:bg-primary-50 rounded"
                                    title="Voir le document"
                                  >
                                    <Eye size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleMarkDocumentForDeletion(doc.id)}
                                    className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                                    title="Marquer pour suppression"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => handleUnmarkDocumentForDeletion(doc.id)}
                                  className="p-1.5 text-green-500 hover:bg-green-50 rounded"
                                  title="Annuler la suppression"
                                >
                                  <RotateCcw size={16} />
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-gray-500">
                      <FileText size={40} className="mx-auto mb-3 text-gray-300" />
                      <p className="text-sm">Aucun document uploadé</p>
                    </div>
                  )}
                </CollapsibleSection>

                {/* Decision Section */}
                <div className="border-t pt-6 mt-6">
                  <h4 className="font-semibold text-gray-900 mb-4">Décision de validation</h4>

                  {/* Summary of changes */}
                  {(hasDeletions || hasModifications) && (
                    <div className="mb-4 space-y-2">
                      {fieldsMarkedForDeletion.length > 0 && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm font-medium text-red-800 mb-2">
                            {fieldsMarkedForDeletion.length} champ(s) à effacer:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {fieldsMarkedForDeletion.map((field) => (
                              <span
                                key={field}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full"
                              >
                                {FIELD_LABELS[field] || field}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {Object.keys(modifiedFields).length > 0 && (
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-sm font-medium text-blue-800 mb-2">
                            {Object.keys(modifiedFields).length} champ(s) modifié(s):
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {Object.keys(modifiedFields).map((field) => (
                              <span
                                key={field}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full"
                              >
                                {FIELD_LABELS[field] || field}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Decision Buttons */}
                  <div className="flex gap-4 mb-4">
                    <button
                      onClick={() => setDecision('APPROVED')}
                      disabled={hasDeletions}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                        decision === 'APPROVED'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : hasDeletions
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'border-gray-200 hover:border-green-300 hover:bg-green-50'
                      }`}
                    >
                      <CheckCircle size={20} />
                      <span className="font-medium">Approuver</span>
                      {hasModifications && !hasDeletions && (
                        <span className="text-xs">(avec modifications)</span>
                      )}
                    </button>
                    <button
                      onClick={() => setDecision('REJECTED')}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                        decision === 'REJECTED'
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200 hover:border-red-300 hover:bg-red-50'
                      }`}
                    >
                      <XCircle size={20} />
                      <span className="font-medium">Rejeter</span>
                      {hasDeletions && (
                        <span className="text-xs">(avec effacements)</span>
                      )}
                    </button>
                  </div>

                  {/* Rejection Details */}
                  {decision === 'REJECTED' && (
                    <div className="space-y-4 bg-red-50 rounded-lg p-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Motif du rejet (optionnel)
                        </label>
                        <textarea
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          placeholder="Expliquez pourquoi le profil est rejeté et ce que l'utilisateur doit corriger..."
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}

                  {/* Error Message */}
                  {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                      <AlertTriangle size={16} />
                      <span className="text-sm">{error}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-500">
              {fieldsMarkedForDeletion.length > 0 && (
                <span className="text-red-600">{fieldsMarkedForDeletion.length} champ(s) à effacer</span>
              )}
              {fieldsMarkedForDeletion.length > 0 && Object.keys(modifiedFields).length > 0 && ' • '}
              {Object.keys(modifiedFields).length > 0 && (
                <span className="text-blue-600">{Object.keys(modifiedFields).length} champ(s) modifié(s)</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSubmitReview}
                disabled={!decision || submitting}
                className={`px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                  decision === 'APPROVED'
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : decision === 'REJECTED'
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-300 text-gray-500'
                }`}
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Traitement...
                  </>
                ) : decision === 'APPROVED' ? (
                  <>
                    <CheckCircle size={16} />
                    Valider le profil
                  </>
                ) : decision === 'REJECTED' ? (
                  <>
                    <XCircle size={16} />
                    Rejeter le profil
                  </>
                ) : (
                  'Sélectionnez une décision'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewer
          isOpen={!!viewingDocument}
          onClose={() => setViewingDocument(null)}
          documentUrl={viewingDocument.url}
          documentName={viewingDocument.name}
          documentType={viewingDocument.type}
        />
      )}
    </div>
  );
};

export default ProfileValidationModal;
